// ABOUTME: Photo gallery component for a return or inbound line
// ABOUTME: Splits warehouse (deletable, with add buttons) from consumer photos (non-deletable)

import { Box, ButtonBase, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from "react-i18next";
import { tokens } from "../../../theme";
import Subheading from "../text/Subheading";
import Shbutton from "../buttons/Shbutton";

const LinePhotos = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const {
        photos = [],
        onAddFromPC,
        onAddFromScanner,
        onDelete,
        onPhotoClick,
        isLoading = false,
        showScanner = true,
    } = props;

    // Keep the original index so the full-screen viewer still lines up with the full photo list.
    const indexed = photos.map((photo, index) => ({ photo, index }));
    const warehousePhotos = indexed.filter(({ photo }) => photo.source !== "consumer");
    const consumerPhotos = indexed.filter(({ photo }) => photo.source === "consumer");

    const countBadge = (count) => (
        <Box
            sx={{
                textAlign: 'center',
                lineHeight: "24px",
                width: 24,
                height: 24,
                borderRadius: 4,
                background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
            }}
        >
            {count}
        </Box>
    );

    const renderGrid = (items, deletable) => (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 1.5,
                mb: 2,
            }}
        >
            {items.map(({ photo, index }) => (
                <Box
                    key={photo.id}
                    sx={{
                        position: "relative",
                        height: 120,
                        borderRadius: 3,
                        overflow: "hidden",
                        border: `1px solid ${colors.grey[200]}`,
                    }}
                >
                    <ButtonBase
                        onClick={() => onPhotoClick && onPhotoClick(index)}
                        sx={{
                            width: "100%",
                            height: "100%",
                            padding: 0,
                            display: "block",
                        }}
                    >
                        <Box
                            component="img"
                            src={photo.url}
                            alt=""
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                    </ButtonBase>
                    {deletable &&
                        <ButtonBase
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(photo.id);
                            }}
                            disabled={isLoading}
                            sx={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                background: colors.red[400],
                                color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt['light'],
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "&:hover": {
                                    background: colors.red[500],
                                },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </ButtonBase>
                    }
                </Box>
            ))}
        </Box>
    );

    const cardSx = {
        p: 2,
        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
        border: theme.palette.mode === "light" ? `1px solid ${colors.grey[100]}` : null,
        boxShadow: theme.palette.mode === "light" ? "0 2px 4px rgba(49,65,95,.15)" : null,
        borderRadius: 4,
    };

    return (
        <Box sx={{ mt: 3 }}>
            <Subheading sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                {t("photos")}
                {warehousePhotos.length > 0 && countBadge(warehousePhotos.length)}
            </Subheading>
            <Box sx={cardSx}>
                {warehousePhotos.length > 0 && renderGrid(warehousePhotos, true)}

                <Box
                    sx={{
                        width: 1,
                        display: "flex",
                        borderRadius: 3,
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                    }}
                >
                    <Shbutton
                        isLoading={isLoading}
                        onClick={onAddFromPC}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={{
                            height: 40,
                            fontSize: 14,
                            flex: 1,
                        }}
                    >
                        PC
                    </Shbutton>
                    {showScanner &&
                        <Shbutton
                            isLoading={isLoading}
                            onClick={onAddFromScanner}
                            color="grey"
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{
                                flex: 1,
                                height: 40,
                                fontSize: 14,
                            }}
                        >
                            {t("scanner")}
                        </Shbutton>
                    }
                </Box>
            </Box>

            {consumerPhotos.length > 0 &&
                <Box sx={{ mt: 3 }}>
                    <Subheading sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                        {t("consumer_photos")}
                        {countBadge(consumerPhotos.length)}
                    </Subheading>
                    <Box sx={cardSx}>
                        {renderGrid(consumerPhotos, false)}
                    </Box>
                </Box>
            }
        </Box>
    );
};

export default LinePhotos;
